import { delay, http, HttpResponse } from "msw";
import { createTripDetailsResponseMock } from "./trips-mock.js";
import { apiPaths } from "../../api-paths.js";
import type { Trips_GetTrip_GetTripQueryResponse_Success } from "../../swagger-types/v1/index.js";

interface GetTripHandlerFactoryOptions {
  delayMs?: number | "infinite";
  dealDates?: { checkIn: DateOnly; checkOut: DateOnly };
}

const waitForFactoryDelay = async ({ delayMs }: GetTripHandlerFactoryOptions = {}) => {
  if (delayMs === "infinite") {
    await delay("infinite");
    return;
  }

  if (typeof delayMs === "number" && delayMs > 0) {
    await delay(delayMs);
  }
};

const waitUntilRequestAborted = async (request: Request) => {
  await new Promise<void>((resolve) => {
    request.signal.addEventListener("abort", () => resolve(), { once: true });
  });
};

const getTripKeyFromRequest = async (request: Request): Promise<string | null> => {
  const body = (await request.json()) as { tripKey?: string };
  return body.tripKey ?? null;
};

const getConfirmedTripHandler = [
  http.post(`**${apiPaths.trips.get}`, () => {
    return HttpResponse.json(createTripDetailsResponseMock("Confirmed"), { status: 200 });
  }),
];

const createConfirmedTripWithCreditsResponse = () => {
  const response = createTripDetailsResponseMock("Confirmed");
  const creditsAmountInUsd = 50;
  response.trip.paymentPricing = {
    ...response.trip.paymentPricing,
    paidPrice: {
      ...response.trip.paymentPricing.paidPrice,
      amount: response.trip.paymentPricing.paidPrice.amount - creditsAmountInUsd,
    },
    paidPriceWithHotelFees: {
      ...response.trip.paymentPricing.paidPriceWithHotelFees,
      amount: response.trip.paymentPricing.paidPriceWithHotelFees.amount - creditsAmountInUsd,
    },
    credits: [{ amount: { amount: creditsAmountInUsd, currency: "USD" }, code: "C0DE123" }],
  };
  response.trip.pricing = {
    ...response.trip.pricing,
    paidPrice: {
      ...response.trip.pricing.paidPrice,
      amount: response.trip.pricing.paidPrice.amount - creditsAmountInUsd,
    },
    paidPriceWithHotelFees: {
      ...response.trip.pricing.paidPriceWithHotelFees,
      amount: response.trip.pricing.paidPriceWithHotelFees.amount - creditsAmountInUsd,
    },
    credits: [{ amount: { amount: creditsAmountInUsd, currency: "USD" }, code: "C0DE123" }],
  };
  return response;
};

const getConfirmedTripWithCreditsHandler = [
  http.post(`**${apiPaths.trips.get}`, () => {
    return HttpResponse.json(createConfirmedTripWithCreditsResponse(), { status: 200 });
  }),
];

const getConfirmedTripFullyPaidWithCreditsHandler = [
  http.post(`**${apiPaths.trips.get}`, () => {
    const response = createTripDetailsResponseMock("Confirmed");
    const creditsAmountInUsd = response.trip.paymentPricing.totalPriceWithoutHotelFees.amount;
    response.trip.paymentPricing = {
      ...response.trip.paymentPricing,
      paidPrice: {
        ...response.trip.paymentPricing.paidPrice,
        amount: response.trip.paymentPricing.paidPrice.amount - creditsAmountInUsd,
      },
      paidPriceWithHotelFees: {
        ...response.trip.paymentPricing.paidPriceWithHotelFees,
        amount: response.trip.paymentPricing.paidPriceWithHotelFees.amount - creditsAmountInUsd,
      },
      credits: [{ amount: { amount: creditsAmountInUsd, currency: "USD" }, code: "C0DE123" }],
    };
    response.trip.pricing = {
      ...response.trip.pricing,
      paidPrice: {
        ...response.trip.pricing.paidPrice,
        amount: response.trip.pricing.paidPrice.amount - creditsAmountInUsd,
      },
      paidPriceWithHotelFees: {
        ...response.trip.pricing.paidPriceWithHotelFees,
        amount: response.trip.pricing.paidPriceWithHotelFees.amount - creditsAmountInUsd,
      },
      credits: [{ amount: { amount: creditsAmountInUsd, currency: "USD" }, code: "C0DE123" }],
    };
    return HttpResponse.json(response, { status: 200 });
  }),
];

const getConfirmedTripWithNoHotelConfirmationNumberHandler = [
  http.post(`**${apiPaths.trips.get}`, () => {
    const response = createTripDetailsResponseMock("Confirmed");
    response.trip.deal.hotelConfirmationNumber = null;
    return HttpResponse.json(response, { status: 200 });
  }),
];

const getConfirmedTripWithDifferentCurrenciesHandler = [
  http.post(`**${apiPaths.trips.get}`, () => {
    const response = createTripDetailsResponseMock("Confirmed");
    response.trip.paymentPricing = {
      totalProductsPrice: { amount: 0.0, currency: "EUR" },
      totalPriceWithoutHotelFees: { amount: 249.33, currency: "EUR" },
      totalPrice: { amount: 319.85, currency: "EUR" },
      currency: "EUR",
      paidPrice: { amount: 249.33, currency: "EUR" },
      paidPriceWithHotelFees: { amount: 319.85, currency: "EUR" },
      credits: [],
    };
    return HttpResponse.json(response, { status: 200 });
  }),
];

const createConfirmedTripNonRefundableResponse = (dealDates?: GetTripHandlerFactoryOptions["dealDates"]) => {
  const response = createTripDetailsResponseMock("Confirmed");
  if (dealDates) {
    response.trip.deal.checkIn = dealDates.checkIn;
    response.trip.deal.checkOut = dealDates.checkOut;
  }
  response.trip.deal.cancellationPolicy = {
    type: "NonRefundable",
    deadline: null,
    description: "",
  };

  return response;
};

const getConfirmedTripNonRefundable = [
  http.post(`**${apiPaths.trips.get}`, () => {
    return HttpResponse.json(createConfirmedTripNonRefundableResponse(), { status: 200 });
  }),
];

const getConfirmedTripPartiallyRefundable = [
  http.post(`**${apiPaths.trips.get}`, () => {
    const response = createTripDetailsResponseMock("Confirmed");
    response.trip.deal.cancellationPolicy.type = "PartiallyRefundable";
    return HttpResponse.json(response, { status: 200 });
  }),
];

const getConfirmedTripPartiallyRefundablePastDeadline = [
  http.post(`**${apiPaths.trips.get}`, () => {
    const response = createTripDetailsResponseMock("Confirmed");
    response.trip.deal.cancellationPolicy = {
      type: "PartiallyRefundable",
      deadline: "2024-12-05T12:00:00",
      description: "",
    };
    return HttpResponse.json(response, { status: 200 });
  }),
];

const getInProcessTripHandler = [
  http.post(`**${apiPaths.trips.get}`, () => {
    return HttpResponse.json(createTripDetailsResponseMock(), { status: 200 });
  }),
];

const getCanceledTripHandler = [
  http.post(`**${apiPaths.trips.get}`, async ({ request }) => {
    const tripKey = await getTripKeyFromRequest(request);

    return HttpResponse.json(createTripDetailsResponseMock("Cancelled", tripKey), { status: 200 });
  }),
];

const getConfirmedTripAfterPollingHandler = [
  http.post(
    `**${apiPaths.trips.get}`,
    () => {
      return HttpResponse.json(createTripDetailsResponseMock(), { status: 200 });
    },
    { once: true },
  ),
  http.post(`**${apiPaths.trips.get}`, () => {
    return HttpResponse.json(createTripDetailsResponseMock("Confirmed"), { status: 200 });
  }),
];

const getConfirmedTripWithCancellationInsuranceHandler = [
  http.post(`**${apiPaths.trips.get}`, () => {
    const response = createTripDetailsResponseMock("Confirmed");
    response.trip.deal.cancellationPolicy = {
      type: "NonRefundable",
      deadline: null,
      description: "",
    };
    response.trip.deal.cancellationInsurance = {
      price: {
        amount: 30,
        currency: "USD",
      },
      paymentPrice: {
        amount: 30,
        currency: "USD",
      },
      policyId: "123",
      policyRefundUrl: "https://www.axa.com/",
      policyKey: "axa-321",
      provider: "CoverGenius",
      policyTermsUrl: "https://www.axa.com/",
      disclaimer: "Our lawyers will try their best to invalidate your refund!",
    };
    response.trip.ancillaries = [
      {
        category: "Tip",
        type: "Tip",
        price: {
          amount: 7.47,
          currency: "USD",
        },
      },
      {
        category: "Package",
        type: "VipService",
        price: {
          amount: 9.99,
          currency: "USD",
        },
      },
    ];

    return HttpResponse.json(response, { status: 200 });
  }),
];

const getTrip200Handler = [
  http.post(`**${apiPaths.trips.get}`, async ({ request }) => {
    const { tripKey } = (await request.json()) as { tripKey?: string };
    return HttpResponse.json(createTripDetailsResponseMock("Confirmed", tripKey), { status: 200 });
  }),
];

const createGetTrip200HandlerByStatus = (
  status: Parameters<typeof createTripDetailsResponseMock>[0],
  options: GetTripHandlerFactoryOptions = {},
) => [
  http.post(`**${apiPaths.trips.get}`, async () => {
    await waitForFactoryDelay(options);
    return HttpResponse.json(createTripDetailsResponseMock(status), { status: 200 });
  }),
];

const getTrip404Handler = [
  http.post(`**${apiPaths.trips.get}`, async () => {
    return HttpResponse.json({}, { status: 404 });
  }),
];

const getTrip500Handler = [
  http.post(`**${apiPaths.trips.get}`, async () => {
    return HttpResponse.json({}, { status: 500 });
  }),
];

const getTrip401Handler = [
  http.post(`**${apiPaths.trips.get}`, async () => {
    await delay(300);
    return HttpResponse.json({ error: "Invalid credentials" }, { status: 401 });
  }),
];

const getTripLoadingHandler = [
  http.post(`**${apiPaths.trips.get}`, async ({ request }) => {
    await waitUntilRequestAborted(request);
    return HttpResponse.json(createTripDetailsResponseMock("Confirmed"), { status: 200 });
  }),
];

const getTripWithInvalidResponseHandler = [
  http.post(`**${apiPaths.trips.get}`, () => {
    const response = createTripDetailsResponseMock("Confirmed");
    response.trip.deal.guest.firstName = undefined as unknown as string;

    return HttpResponse.json(response, { status: 200 });
  }),
];

const get200InAttemptTripHandler = [
  http.post(`**${apiPaths.trips.get}`, async () => {
    const confirmedTrip = createTripDetailsResponseMock("Confirmed");

    const trip: Trips_GetTrip_GetTripQueryResponse_Success = {
      ...confirmedTrip,
      trip: { ...confirmedTrip.trip, status: "InAttempt" },
    };

    return HttpResponse.json(trip, { status: 200 });
  }),
];

const createConfirmedFreeCancellationTripResponse = (
  tripKey: string | null,
): Trips_GetTrip_GetTripQueryResponse_Success => {
  const confirmedTrip = createTripDetailsResponseMock("Confirmed", tripKey);

  return {
    ...confirmedTrip,
    trip: {
      ...confirmedTrip.trip,
      deal: {
        ...confirmedTrip.trip.deal,
        cancellationPolicy: {
          type: "Refundable",
          deadline: "2066-02-21T00:00:00.000+01:00",
          description: "",
        },
      },
    },
  };
};

const get200ConfirmedFreeCancellationTripHandler = [
  http.post(`**${apiPaths.trips.get}`, async ({ request }) => {
    const tripKey = await getTripKeyFromRequest(request);

    return HttpResponse.json(createConfirmedFreeCancellationTripResponse(tripKey), { status: 200 });
  }),
];

const get200CompletedTripHandler = [
  http.post(`**${apiPaths.trips.get}`, async () => {
    const confirmedTrip = createTripDetailsResponseMock("Confirmed");

    const trip: Trips_GetTrip_GetTripQueryResponse_Success = {
      ...confirmedTrip,
      trip: {
        ...confirmedTrip.trip,
        deal: {
          ...confirmedTrip.trip.deal,
          checkIn: "1995-01-01",
          checkOut: "1995-01-02",
        },
      },
    };
    return HttpResponse.json(trip, { status: 200 });
  }),
];

const tripBoardCodeDescriptions = {
  RoomOnly: "Room Only",
  BedAndBreakfast: "Breakfast Included",
  HalfBoard: "Half Board",
  FullBoard: "Full Board",
  AllInclusive: "All Inclusive",
} as const;

type TripBoardCode = keyof typeof tripBoardCodeDescriptions;

const createConfirmedTripByBoardCode = (boardCode: TripBoardCode): Trips_GetTrip_GetTripQueryResponse_Success => {
  const confirmedTrip = createTripDetailsResponseMock("Confirmed");

  return {
    ...confirmedTrip,
    trip: {
      ...confirmedTrip.trip,
      deal: {
        ...confirmedTrip.trip.deal,
        boardCode,
        boardDescription: tripBoardCodeDescriptions[boardCode],
      },
    },
  };
};

const createGet200ConfirmedTripHandler = (options: GetTripHandlerFactoryOptions = {}) => [
  http.post(`**${apiPaths.trips.get}`, async () => {
    await waitForFactoryDelay(options);
    return HttpResponse.json(createTripDetailsResponseMock("Confirmed"), { status: 200 });
  }),
];

const createGet200TripWithCheckInHandler = (
  status: Parameters<typeof createTripDetailsResponseMock>[0],
  checkIn: DateOnly,
  options: GetTripHandlerFactoryOptions = {},
) => [
  http.post(`**${apiPaths.trips.get}`, async ({ request }) => {
    await waitForFactoryDelay(options);
    const tripKey = await getTripKeyFromRequest(request);
    const tripResponse = createTripDetailsResponseMock(status, tripKey);

    return HttpResponse.json(
      {
        ...tripResponse,
        trip: {
          ...tripResponse.trip,
          deal: {
            ...tripResponse.trip.deal,
            checkIn,
          },
        },
      } satisfies Trips_GetTrip_GetTripQueryResponse_Success,
      { status: 200 },
    );
  }),
];

const createGet200ConfirmedTripWithCheckInHandler = (checkIn: DateOnly, options: GetTripHandlerFactoryOptions = {}) =>
  createGet200TripWithCheckInHandler("Confirmed", checkIn, options);

const createGet200ConfirmedTripNonRefundableHandler = (options: GetTripHandlerFactoryOptions = {}) => [
  http.post(`**${apiPaths.trips.get}`, async () => {
    await waitForFactoryDelay(options);
    return HttpResponse.json(createConfirmedTripNonRefundableResponse(options.dealDates), { status: 200 });
  }),
];

const createGet200ConfirmedFreeCancellationTripHandler = (options: GetTripHandlerFactoryOptions = {}) => [
  http.post(`**${apiPaths.trips.get}`, async ({ request }) => {
    await waitForFactoryDelay(options);
    const tripKey = await getTripKeyFromRequest(request);
    return HttpResponse.json(createConfirmedFreeCancellationTripResponse(tripKey), { status: 200 });
  }),
];

const createGet200ConfirmedTripByBoardCodeHandler = (
  boardCode: TripBoardCode,
  options: GetTripHandlerFactoryOptions = {},
) => [
  http.post(`**${apiPaths.trips.get}`, async () => {
    await waitForFactoryDelay(options);
    return HttpResponse.json(createConfirmedTripByBoardCode(boardCode), { status: 200 });
  }),
];

const getConfirmedTripLoadingStateHandler = createGet200ConfirmedTripHandler({ delayMs: 15000 });
const getConfirmedTripNonRefundableLoadingStateHandler = createGet200ConfirmedTripNonRefundableHandler({
  delayMs: 15000,
});
const getConfirmedTripRoomOnlyHandler = createGet200ConfirmedTripByBoardCodeHandler("RoomOnly");
const getConfirmedTripHalfBoardHandler = createGet200ConfirmedTripByBoardCodeHandler("HalfBoard");
const getConfirmedTripFullBoardHandler = createGet200ConfirmedTripByBoardCodeHandler("FullBoard");
const getConfirmedTripRoomOnlyLoadingStateHandler = createGet200ConfirmedTripByBoardCodeHandler("RoomOnly", {
  delayMs: 15000,
});

export const getTripHandlers = {
  get200: getTrip200Handler,
  get404: getTrip404Handler,
  get500: getTrip500Handler,
  get401: getTrip401Handler,
  getLoading: getTripLoadingHandler,
  get200InvalidResponseHandler: getTripWithInvalidResponseHandler,
  get200ConfirmedTripHandler: getConfirmedTripHandler,
  get200ConfirmedTripLoadingStateHandler: getConfirmedTripLoadingStateHandler,
  get200ConfirmedTripWithNoHotelConfirmationNumberHandler: getConfirmedTripWithNoHotelConfirmationNumberHandler,
  get200ConfirmedTripWithDifferentCurrenciesHandler: getConfirmedTripWithDifferentCurrenciesHandler,
  get200ConfirmedFreeCancellationHandler: get200ConfirmedFreeCancellationTripHandler,
  get200ConfirmedTripNonRefundable: getConfirmedTripNonRefundable,
  get200ConfirmedTripNonRefundableLoadingStateHandler: getConfirmedTripNonRefundableLoadingStateHandler,
  get200ConfirmedTripPartiallyRefundable: getConfirmedTripPartiallyRefundable,
  get200ConfirmedTripPartiallyRefundablePastDeadline: getConfirmedTripPartiallyRefundablePastDeadline,
  get200ConfirmedTripRoomOnlyHandler: getConfirmedTripRoomOnlyHandler,
  get200ConfirmedTripHalfBoardHandler: getConfirmedTripHalfBoardHandler,
  get200ConfirmedTripFullBoardHandler: getConfirmedTripFullBoardHandler,
  get200ConfirmedTripRoomOnlyLoadingStateHandler: getConfirmedTripRoomOnlyLoadingStateHandler,
  get200InProcessTripHandler: getInProcessTripHandler,
  get200InAttemptTripHandler: get200InAttemptTripHandler,
  get200CanceledTripHandler: getCanceledTripHandler,
  get200ConfirmedTripAfterPollingHandler: getConfirmedTripAfterPollingHandler,
  get200ConfirmedTripWithCancellationInsuranceHandler: getConfirmedTripWithCancellationInsuranceHandler,
  get200CompletedTripHandler: get200CompletedTripHandler,
  get200ConfirmedTripWithCreditsHandler: getConfirmedTripWithCreditsHandler,
  get200ConfirmedTripFullyPaidWithCreditsHandler: getConfirmedTripFullyPaidWithCreditsHandler,
} as const;

export const getTripHandlersFactory = {
  create200ByStatus: (
    status: Parameters<typeof createTripDetailsResponseMock>[0],
    options?: GetTripHandlerFactoryOptions,
  ) => {
    return createGetTrip200HandlerByStatus(status, options);
  },
  create200ConfirmedTrip: (options?: GetTripHandlerFactoryOptions) => {
    return createGet200ConfirmedTripHandler(options);
  },
  create200TripWithCheckIn: (
    status: Parameters<typeof createTripDetailsResponseMock>[0],
    checkIn: DateOnly,
    options?: GetTripHandlerFactoryOptions,
  ) => {
    return createGet200TripWithCheckInHandler(status, checkIn, options);
  },
  create200ConfirmedTripWithCheckIn: (checkIn: DateOnly, options?: GetTripHandlerFactoryOptions) => {
    return createGet200ConfirmedTripWithCheckInHandler(checkIn, options);
  },
  create200ConfirmedTripNonRefundable: (options?: GetTripHandlerFactoryOptions) => {
    return createGet200ConfirmedTripNonRefundableHandler(options);
  },
  create200ConfirmedFreeCancellationTrip: (options?: GetTripHandlerFactoryOptions) => {
    return createGet200ConfirmedFreeCancellationTripHandler(options);
  },
  create200ConfirmedTripByBoardCode: (boardCode: TripBoardCode, options?: GetTripHandlerFactoryOptions) => {
    return createGet200ConfirmedTripByBoardCodeHandler(boardCode, options);
  },
  create200ConfirmedTripByBoardCodeControlled: (boardCode: TripBoardCode) => {
    const deferredResponse = createDeferred<void>();

    return {
      handlers: [
        http.post(`**${apiPaths.trips.get}`, async () => {
          await deferredResponse.promise;
          return HttpResponse.json(createConfirmedTripByBoardCode(boardCode), { status: 200 });
        }),
      ],
      continueResponse: () => {
        deferredResponse.resolve();
      },
    };
  },
  create200ConfirmedTripWithCreditsControlled: () => {
    const deferredResponse = createDeferred<void>();

    return {
      handlers: [
        http.post(`**${apiPaths.trips.get}`, async () => {
          await deferredResponse.promise;
          return HttpResponse.json(createConfirmedTripWithCreditsResponse(), { status: 200 });
        }),
      ],
      continueResponse: () => {
        deferredResponse.resolve();
      },
    };
  },
  create200ConfirmedTripNonRefundableControlled: () => {
    const deferredResponse = createDeferred<void>();

    return {
      handlers: [
        http.post(`**${apiPaths.trips.get}`, async () => {
          await deferredResponse.promise;
          return HttpResponse.json(createConfirmedTripNonRefundableResponse(), { status: 200 });
        }),
      ],
      continueResponse: () => {
        deferredResponse.resolve();
      },
    };
  },
} as const;

const createDeferred = <T>() => {
  let resolve!: (value: T | PromiseLike<T>) => void;
  const promise = new Promise<T>((resolver) => {
    resolve = resolver;
  });

  return {
    promise,
    resolve,
  };
};
